import * as yup from 'yup';

import { AffiliatePersonnel, ClinicalAffiliate, ProgramPersonnelDto } from '../common/Types';
import { Button, Col, Form, FormGroup, Input, InputGroup, InputGroupText, Label, Row } from 'reactstrap';
import { Countries, SatelliteType, SatelliteTypes, States } from '../common/Data';
import { useFieldArray, useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import React, { useCallback, useMemo } from 'react';
import { programService } from '../services/ProgramService';
import { toast } from 'react-toastify';
import moment from 'moment';

interface ClinicalAffiliateFormProps {
    onSave: () => void;
    onCancel: () => void;
    programId: number;
    affiliate?: ClinicalAffiliate;
    canEdit: boolean;
    personnel: ProgramPersonnelDto[];
    reportYear: number;
}

// Form data interface with proper typing
interface ClinicalAffiliateFormData {
    satelliteId: number;
    programId: number;
    title?: string;
    city?: string;
    state?: string;
    country?: string;
    satalliteTypeId: number;
    address?: string;
    address2?: string;
    postalCode?: string;
    dateCreated: string;
    isActive: boolean;
    personnel: AffiliatePersonnel[];
}

const ClinicalAffiliateForm = (props: ClinicalAffiliateFormProps) => {
    // Memoize validation schema
    const clinicalAffiliateSchema = useMemo(
        () =>
            yup.object<ClinicalAffiliateFormData>({
                satelliteId: yup
                    .number()
                    .required()
                    .default(props.affiliate?.satelliteId || 0),
                programId: yup.number().required().default(props.programId),
                title: yup.string().optional().nullable(),
                city: yup.string().optional().nullable(),
                state: yup.string().optional().nullable(),
                country: yup.string().optional().nullable(),
                satalliteTypeId: yup.number().required().default(SatelliteType['Clinical Affiliate']),
                address: yup.string().optional().nullable(),
                address2: yup.string().optional().nullable(),
                postalCode: yup.string().optional().nullable(),
                dateCreated: yup.string().required(),
                isActive: yup.boolean().required().default(true),
                personnel: yup
                    .array()
                    .of(
                        yup.object<AffiliatePersonnel>({
                            satelliteId: yup.number().required(),
                            programPersonnelId: yup
                                .number()
                                .required()
                                .moreThan(0, 'Please select a person'),
                            reportYear: yup.number().required(),
                        }),
                    )
                    .default([]),
            }),
        [props.affiliate, props.programId, props.reportYear],
    );

    const {
        handleSubmit,
        control,
        watch,
        setValue,
        formState: { isSubmitting, errors, isDirty },
    } = useForm<ClinicalAffiliateFormData>({
        resolver: yupResolver(clinicalAffiliateSchema) as any,
        mode: 'onChange',
        defaultValues: {
            satelliteId: props.affiliate?.satelliteId || 0,
            programId: props.programId,
            title: props.affiliate?.title,
            city: props.affiliate?.city,
            state: props.affiliate?.state,
            country: props.affiliate?.country || 'United States of America',
            satalliteTypeId: SatelliteTypes['Clinical Affiliate'],
            address: props.affiliate?.address,
            address2: props.affiliate?.address2,
            postalCode: props.affiliate?.postalCode,
            dateCreated: props.affiliate?.dateCreated
                ? moment.utc(props.affiliate.dateCreated).format('YYYY-MM-DD')
                : moment().format('YYYY-MM-DD'),
            isActive: props.affiliate?.isActive ?? true,
            personnel: props.affiliate?.personnel || [],
        },
    });
    const { fields, append, remove } = useFieldArray({
        control,
        name: 'personnel',
    });

    const onSubmit = useCallback(
        async (values: ClinicalAffiliateFormData): Promise<void> => {
            // Skip if no changes for existing records
            if (props.affiliate && !isDirty) {
                props.onCancel();
                return;
            }

            const toastId = toast.info('Saving Clinical Affiliate...');

            try {
                // Convert form data to API format
                const apiData: ClinicalAffiliate = {
                    satelliteId: values.satelliteId,
                    programId: values.programId,
                    title: values.title,
                    city: values.city,
                    state: values.state,
                    country: values.country,
                    satalliteTypeId: values.satalliteTypeId,
                    address: values.address,
                    address2: values.address2,
                    postalCode: values.postalCode,
                    dateCreated: values.dateCreated ? new Date(values.dateCreated) : new Date(),
                    isActive: values.isActive,
                    personnel: values.personnel,
                };

                await programService.saveClinicalAffiliate(apiData, props.reportYear);

                toast.update(toastId, {
                    type: 'success',
                    render: 'Clinical Affiliate saved successfully',
                    autoClose: 3000,
                });

                props.onSave();
            } catch (error) {
                console.error('Save error:', error);
                toast.update(toastId, {
                    type: 'error',
                    render: 'Error saving Clinical Affiliate',
                    autoClose: 5000,
                });
            }
        },
        [isDirty, props],
    );

    const formatPersonText = useCallback((personnel: ProgramPersonnelDto): string => {
        return `${personnel.personnelType ? `${personnel.personnelType.title}: ` : ``}
        ${personnel.people?.firstName} ${personnel.people?.lastName}
        ${personnel.people?.city ? `, ${personnel.people.city}` : ``}
        ${personnel.people?.state ? `, ${personnel.people.state}` : ``}`;
    }, []);

    const watchIsActive = watch('isActive');

    const handleToggleActive = useCallback(() => {
        setValue('isActive', false, { shouldDirty: true });
    }, [setValue]);

    const handleAppendPersonnel = useCallback(() => {
        append({
            satelliteId: props.affiliate?.satelliteId || 0,
            reportYear: +props.reportYear,
            programPersonnelId: 0,
        });
    }, [append, props.affiliate?.satelliteId, props.reportYear]);

    return (
        <Form noValidate onSubmit={handleSubmit(onSubmit)}>
            <Controller
                name="isActive"
                control={control}
                render={({ field }) => <input type="hidden" {...field} value={field.value ? 'true' : 'false'} />}
            />
            {!props.affiliate && (
                <Row>
                    <Col>
                        <h5 className={'card-title'}>Clinical Affiliate Detail</h5>
                    </Col>
                </Row>
            )}
            <Row>
                <Col>
                    <FormGroup>
                        <Label>Name</Label>
                        <Controller
                            name="title"
                            control={control}
                            render={({ field }) => (
                                <Input
                                    bsSize="sm"
                                    {...field}
                                    value={field.value || ''}
                                    onChange={(e) => field.onChange(e.target.value || null)}
                                />
                            )}
                        />
                        {errors.title && <span className="text-danger">{errors.title.message}</span>}
                    </FormGroup>
                </Col>
            </Row>
            <Row>
                <Col xs={12} md={6}>
                    <Row>
                        <Col xs={12}>
                            <FormGroup>
                                <Label>Address</Label>
                                <Controller
                                    name="address"
                                    control={control}
                                    render={({ field }) => (
                                        <Input
                                            bsSize="sm"
                                            {...field}
                                            value={field.value || ''}
                                            onChange={(e) => field.onChange(e.target.value || undefined)}
                                        />
                                    )}
                                />
                                {errors.address && <span className="text-danger">{errors.address.message}</span>}
                            </FormGroup>
                        </Col>
                        <Col xs={12}>
                            <FormGroup>
                                <Label>Address 2</Label>
                                <Controller
                                    name="address2"
                                    control={control}
                                    render={({ field }) => (
                                        <Input
                                            bsSize="sm"
                                            {...field}
                                            value={field.value || ''}
                                            onChange={(e) => field.onChange(e.target.value || undefined)}
                                        />
                                    )}
                                />
                                {errors.address2 && <span className="text-danger">{errors.address2.message}</span>}
                            </FormGroup>
                        </Col>
                    </Row>
                </Col>
                <Col xs={12} md={6}>
                    <Row>
                        <Col xs={12} md={6}>
                            <FormGroup>
                                <Label>City</Label>
                                <Controller
                                    name="city"
                                    control={control}
                                    render={({ field }) => (
                                        <Input
                                            bsSize="sm"
                                            {...field}
                                            value={field.value || ''}
                                            onChange={(e) => field.onChange(e.target.value || undefined)}
                                        />
                                    )}
                                />
                                {errors.city && <span className="text-danger">{errors.city.message}</span>}
                            </FormGroup>
                        </Col>
                        <Col xs={12} md={6}>
                            <FormGroup>
                                <Label>State</Label>
                                <Controller
                                    name="state"
                                    control={control}
                                    render={({ field }) => (
                                        <Input
                                            type="select"
                                            bsSize="sm"
                                            {...field}
                                            value={field.value || ''}
                                            onChange={(e) => field.onChange(e.target.value || undefined)}
                                        >
                                            <option value="">{``}</option>
                                            {States &&
                                                States.map((s) => (
                                                    <option key={s.abbreviation} value={s.abbreviation}>
                                                        {s.abbreviation}
                                                    </option>
                                                ))}
                                        </Input>
                                    )}
                                />
                                {errors.state && <span className="text-danger">{errors.state.message}</span>}
                            </FormGroup>
                        </Col>
                        <Col xs={12} md={6}>
                            <FormGroup>
                                <Label>Zip / Postal Code</Label>
                                <Controller
                                    name="postalCode"
                                    control={control}
                                    render={({ field }) => (
                                        <Input
                                            bsSize="sm"
                                            {...field}
                                            value={field.value || ''}
                                            onChange={(e) => field.onChange(e.target.value || undefined)}
                                        />
                                    )}
                                />
                                {errors.postalCode && <span className="text-danger">{errors.postalCode.message}</span>}
                            </FormGroup>
                        </Col>
                        <Col xs={12} md={6}>
                            <FormGroup>
                                <Label>Country</Label>
                                <Controller
                                    name="country"
                                    control={control}
                                    render={({ field }) => (
                                        <Input
                                            type="select"
                                            bsSize="sm"
                                            {...field}
                                            value={field.value || ''}
                                            onChange={(e) => field.onChange(e.target.value || undefined)}
                                        >
                                            <option value="">{``}</option>
                                            {Countries &&
                                                Countries.map((c) => (
                                                    <option key={c} value={c}>
                                                        {c}
                                                    </option>
                                                ))}
                                        </Input>
                                    )}
                                />
                                {errors.country && <span className="text-danger">{errors.country.message}</span>}
                            </FormGroup>
                        </Col>
                    </Row>
                </Col>
            </Row>
            <Row>
                <Col xs={12}>
                    <Row className={'mb-2'}>
                        <Col>
                            <h5 className={'card-title'}>Clinical Affiliate Personnel</h5>
                            <h6
                                className={'card-subtitle text-muted'}
                            >{`Please select personnel to associate to this Clinical Affiliate`}</h6>
                        </Col>
                    </Row>
                    {fields.map((ap, i) => (
                        <FormGroup key={ap.id}>
                            <Controller
                                name={`personnel.${i}.satelliteId`}
                                control={control}
                                render={({ field }) => <input type="hidden" {...field} />}
                            />
                            <Controller
                                name={`personnel.${i}.reportYear`}
                                control={control}
                                render={({ field }) => <input type="hidden" {...field} />}
                            />
                            <InputGroup>
                                <InputGroupText>
                                    <Button type="button" color="danger" onClick={() => remove(i)}>
                                        <i className="mdi mdi-close" />
                                    </Button>
                                </InputGroupText>
                                <Controller
                                    name={`personnel.${i}.programPersonnelId`}
                                    control={control}
                                    render={({ field }) => (
                                        <Input
                                            type="select"
                                            {...field}
                                            value={field.value || 0}
                                            onChange={(e) => field.onChange(Number(e.target.value))}
                                        >
                                            <option value={0}>Select personnel...</option>
                                            {props.personnel &&
                                                props.personnel.map((person) => (
                                                    <option
                                                        key={person.programPersonnelId}
                                                        value={person.programPersonnelId}
                                                    >
                                                        {formatPersonText(person)}
                                                    </option>
                                                ))}
                                        </Input>
                                    )}
                                />
                            </InputGroup>
                            {errors.personnel?.[i]?.programPersonnelId && (
                                <span className="text-danger">{errors.personnel[i]?.programPersonnelId?.message}</span>
                            )}
                        </FormGroup>
                    ))}

                    {props.personnel && props.personnel.length > 0 && (
                        <Button type="button" color="info" onClick={handleAppendPersonnel}>
                            <i className="mdi mdi-plus" />
                            <span className="ms-1">Add Person</span>
                        </Button>
                    )}
                </Col>
            </Row>

            {props.canEdit && (
                <Row className={'mt-2'}>
                    <Col xs={12} className={'d-flex flex-row-reverse'}>
                        <Button type="button" color={'link'} onClick={props.onCancel}>
                            Cancel
                        </Button>
                        <Button disabled={isSubmitting} type="submit" color={'primary'}>
                            Save
                        </Button>
                        {props.affiliate && (
                            <Button
                                type="button"
                                color={watchIsActive ? 'danger' : 'secondary'}
                                className={'me-5'}
                                onClick={handleToggleActive}
                            >
                                <i className={'mdi mdi-delete'} />
                                {watchIsActive && <span className={'ms-1'}>Remove Affiliate</span>}
                                {!watchIsActive && <span className={'ms-1'}>Click Save to Confirm</span>}
                            </Button>
                        )}
                    </Col>
                </Row>
            )}
        </Form>
    );
};

export default ClinicalAffiliateForm;
